/* @jsx h */
import { defineComponent, ref, h } from "vue";
import { LemmonModal } from "@lemmonhq/uikit";
import { useLocalization } from "@lemmonhq/uikit";
import { useAuth } from "../../providers/Auth";

interface ManualPaymentModalProps {
  onClose: () => void;
  onConfirm: (
    paymentMethod: string,
    reference: string,
    tip?: number,
  ) => Promise<void>;
  totalAmount: number;
}

export const ManualPaymentModal = defineComponent<ManualPaymentModalProps>({
  name: "ManualPaymentModal",
  props: ["onClose", "onConfirm", "totalAmount"],
  setup(props) {
    const { t } = useLocalization();
    const auth = useAuth();
    const selectedPaymentMethod = ref<string>();
    const reference = ref<string>("");
    const tipAmount = ref("");

    const handleSubmit = async (e: Event) => {
      e.preventDefault();
      if (!selectedPaymentMethod.value) return;
      await props.onConfirm(
        selectedPaymentMethod.value,
        reference.value,
        Number(tipAmount.value) || undefined,
      );
      props.onClose();
    };

    return () => (
      <div>
        <LemmonModal
          title={t("pos.pay.manual_payment")}
          onClose={props.onClose}
          renderButtons={() => (
            <div class="p-4 border-t ">
              <div class="flex justify-between gap-2">
                <button
                  onClick={() => props.onClose()}
                  class="btn btn-neutral"
                  type="button"
                >
                  {t("general.words.cancel")}
                </button>
                <button
                  class="btn btn-primary"
                  type="submit"
                  disabled={!selectedPaymentMethod.value}
                  onClick={handleSubmit}
                >
                  {t("pos.pay.confirm_payment_button")}
                </button>
              </div>
            </div>
          )}
        >
          <div class="space-y-6">
            <div class="flex flex-col gap-0 text-accent">
              <div class="border-b  flex py-6 px-4">
                <div class="flex-[0.5] font-semibold flex items-center text-1xl">
                  {t("pos.pay.confirm_payment_button")}
                </div>
                <div class="flex-1 text-1xl text-right">
                  {auth.user.value?.restaurant.currency || "CHF"}{" "}
                  {props.totalAmount.toFixed(2)}
                </div>
              </div>

              {/* Payment Method Options */}
              <div class="border-b  flex py-6 px-4">
                <div class="flex-[0.5] font-semibold flex items-center text-1xl">
                  {t("pos.pay.payment_method")}
                </div>
                <div class="flex-1 flex flex-wrap gap-2 justify-center">
                  <div class="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      class={`btn ${
                        selectedPaymentMethod.value === "card"
                          ? "btn-primary"
                          : "btn-neutral"
                      }`}
                      onClick={() => (selectedPaymentMethod.value = "card")}
                    >
                      Card
                    </button>
                    <button
                      type="button"
                      class={`btn ${
                        selectedPaymentMethod.value === "invoice"
                          ? "btn-primary"
                          : "btn-neutral"
                      }`}
                      onClick={() => (selectedPaymentMethod.value = "invoice")}
                    >
                      {t("pos.pay.invoice_button")}
                    </button>
                    <button
                      type="button"
                      class={`btn ${
                        selectedPaymentMethod.value === "qr"
                          ? "btn-primary"
                          : "btn-neutral"
                      }`}
                      onClick={() => (selectedPaymentMethod.value = "qr")}
                    >
                      QR
                    </button>
                    <button
                      type="button"
                      class={`btn ${
                        selectedPaymentMethod.value === "twint"
                          ? "btn-primary"
                          : "btn-neutral"
                      }`}
                      onClick={() => (selectedPaymentMethod.value = "twint")}
                    >
                      Twint
                    </button>
                    <button
                      type="button"
                      class={`btn ${
                        selectedPaymentMethod.value === "lunch_check"
                          ? "btn-primary"
                          : "btn-neutral"
                      }`}
                      onClick={() =>
                        (selectedPaymentMethod.value = "lunch_check")
                      }
                    >
                      Lunch Check
                    </button>
                    <button
                      type="button"
                      class={`btn ${
                        selectedPaymentMethod.value === "other"
                          ? "btn-primary"
                          : "btn-neutral"
                      }`}
                      onClick={() => (selectedPaymentMethod.value = "other")}
                    >
                      {t("pos.pay.other_button")}
                    </button>
                  </div>
                </div>
              </div>

              {/* Reference Field */}
              <div class="border-b  flex py-6 px-4">
                <div class="flex-[0.5] font-semibold flex items-center text-1xl">
                  {t("pos.pay.reference")}
                </div>
                <div class="flex-1">
                  <input
                    type="text"
                    v-model={reference.value}
                    class="input input-bordered w-full text-1xl"
                    placeholder="Enter reference"
                  />
                </div>
              </div>
              <div class="border-b  flex py-6 px-4">
                <div class="flex-[0.5] flex font-semibold items-center text-1xl">
                  {t("pos.pay.tip")}
                </div>
                <label class="flex-1 form-control w-full">
                  <input
                    type="number"
                    v-model={tipAmount.value}
                    class="input input-bordered w-full text-1xl"
                    step="0.01"
                    placeholder="0.00"
                  />
                </label>
              </div>
            </div>
          </div>
        </LemmonModal>
      </div>
    );
  },
});
